import { IkeAngle, IkeUnit } from '@ikegps/core-measurement';
import type { DuplicateAffectedField, DuplicateNotification, DuplicateNotificationCode } from '@ikegps/core-api';
import { ModalService } from '../../components/modal/modal.service';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { DuplicateNotificationCtrl } from './duplicate.notification.ctrl';
import { CSS, Style } from './duplicate.notification.style';

addStyles(Style);

const codeToTranslationKey: Record<DuplicateNotificationCode, T> = {
	duplicate_failed: T.COLLECTION_DUPLICATE_NOTIFICATION_FAILED,
	span_demoted_no_partner: T.COLLECTION_DUPLICATE_NOTIFICATION_DEMOTED_NO_PARTNER,
	span_demoted_partner_failed: T.COLLECTION_DUPLICATE_NOTIFICATION_DEMOTED_PARTNER_FAILED,
	span_demoted_no_location: T.COLLECTION_DUPLICATE_NOTIFICATION_DEMOTED_NO_LOCATION,
};

function formatAffectedField(field: DuplicateAffectedField): string {
	const value =
		field.vector != null
			? `${IkeUnit.display(field.vector.distance, Services.UserSettings.units, IkeUnit.METER)} at ${IkeUnit.displayAngle(field.vector.bearing, IkeAngle.DEGREE, IkeAngle.RADIAN)}`
			: field.fieldName;
	return field.pathLabel.length > 0 ? `${field.pathLabel} - ${value}` : value;
}

function viewSection(notifications: DuplicateNotification[]): MithrilVNode[] {
	return notifications.map((n) =>
		m('div', { className: CSS.section }, [
			m('span', { className: CSS.title }, n.sourceTitle || n.sourceUuid),
			m('span', ': '),
			m('span', Translate(codeToTranslationKey[n.code])),
			n.affectedFields && n.affectedFields.length > 0
				? m(
						'ul',
						{ className: CSS.content },
						n.affectedFields.map((field) => m('li', formatAffectedField(field)))
					)
				: null,
		])
	);
}

function viewContent(ctrl: DuplicateNotificationCtrl): MithrilVNode {
	const sections: MithrilVNode[] = [];
	if (ctrl.errors.length > 0) sections.push(...viewSection(ctrl.errors));
	if (ctrl.warnings.length > 0) sections.push(...viewSection(ctrl.warnings));
	return m('div', { className: CSS.container }, sections);
}

function viewTitle(ctrl: DuplicateNotificationCtrl): string {
	return ctrl.hadFailure
		? Translate(T.COLLECTION_DUPLICATE_NOTIFICATION_TITLE_ERRORS)
		: Translate(T.COLLECTION_DUPLICATE_NOTIFICATION_TITLE_WARNINGS);
}

/**
 * Open a follow-up dialog listing per-pole failures + span-demotion warnings emitted by a
 * `task.create` ('duplicate') task. Resolves once the user dismisses the dialog.
 */
export async function showDuplicateNotificationDialog(
	notifications: DuplicateNotification[],
	opts: { hadFailure: boolean }
): Promise<void> {
	const ctrl = new DuplicateNotificationCtrl(notifications, opts);
	await ModalService.open({
		title: viewTitle(ctrl),
		content: viewContent(ctrl),
		hasCancel: false,
		className: CSS.modal,
	});
}

export const DuplicateNotificationContent = { view: viewContent };
export const DuplicateNotificationTitle = { view: viewTitle };
